import type { FlightLocationSuggestion } from "@b2bportal/core-flights";
import { CallState, type WithOptionalProps } from "@b2bportal/core-types";
import type { Suggestion as HotelSuggestion } from "@b2bportal/lodging-api";
import type { DateRange, Travelers } from "@b2bportal/packages-api";
import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import { initializePackages } from "@tenant/packages";
import type { PackageType } from "../../../types";

export interface PackagesSearch {
  origin: FlightLocationSuggestion;
  destination: HotelSuggestion;
  dateRange: DateRange;
  guests: Travelers;
  roomCount: number;
  packageType?: PackageType;
  driverAge?: number;
}

export interface NewPackagesSearch {
  origin?: FlightLocationSuggestion;
  destination?: HotelSuggestion;
  dateRange: WithOptionalProps<DateRange, "from" | "until">;
  guests: Travelers;
  roomCount: number;
  packageType?: PackageType;
  driverAge?: number;
}

export interface PackageSearchState {
  searchFormValues: NewPackagesSearch;
  requestedSearch?: PackagesSearch;
  search?: PackagesSearch;
  initialized?: CallState;
}
export const initialSearchState: PackageSearchState = {
  searchFormValues: {
    dateRange: {},
    guests: {
      adultCount: 2,
      childAges: [],
      infantOnLapAges: [],
      infantInSeatAges: [],
    },
    roomCount: 1,
  },
};

export const packagesSearchSlice = createSlice({
  name: "packages/search",
  initialState: initialSearchState,
  reducers: {
    setNewGuests: (state, action: PayloadAction<Travelers>) => {
      state.searchFormValues.guests = action.payload;
    },
    setNewDestination: (state, action: PayloadAction<HotelSuggestion>) => {
      state.searchFormValues.destination = action.payload;
    },
    setNewOrigin: (state, action: PayloadAction<FlightLocationSuggestion>) => {
      state.searchFormValues.origin = action.payload;
    },
    setNewDateFrom: (state, action: PayloadAction<string | undefined>) => {
      state.searchFormValues.dateRange = {
        ...state.searchFormValues.dateRange,
        from: action.payload,
      };
    },
    setNewDateUntil: (state, action: PayloadAction<string | undefined>) => {
      state.searchFormValues.dateRange = {
        ...state.searchFormValues.dateRange,
        until: action.payload,
      };
    },
    setNewDateRange: (state, action: PayloadAction<DateRange>) => {
      state.searchFormValues.dateRange = action.payload;
    },
    setNewRoomCount: (state, action: PayloadAction<number>) => {
      state.searchFormValues.roomCount = action.payload;
    },
    setRequestedSearch: (state, action: PayloadAction<PackagesSearch>) => {
      state.searchFormValues = action.payload;
      state.requestedSearch = action.payload;
    },
    setSearch: (state, action: PayloadAction<PackagesSearch>) => {
      state.searchFormValues = action.payload;
      state.requestedSearch = action.payload;
      state.search = action.payload;
    },
    setNewPackageType: (state, action: PayloadAction<PackageType>) => {
      state.searchFormValues.packageType = action.payload;
    },
    setDriverAge: (state, action: PayloadAction<number>) => {
      state.searchFormValues.driverAge = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializePackages.pending, (state) => {
        state.initialized = CallState.InProcess;
      })
      .addCase(
        initializePackages.fulfilled,
        (state, action: PayloadAction<PackageSearchState>) => {
          state.initialized = CallState.Success;
          state.requestedSearch = action.payload.requestedSearch;
          state.searchFormValues = action.payload.searchFormValues;
        }
      )
      .addCase(initializePackages.rejected, (state) => {
        state.initialized = CallState.Failed;
      });
  },
});

export const PackagesSearchActions = packagesSearchSlice.actions;
export default packagesSearchSlice.reducer;
